import React, { useState } from 'react';
import './App.css';
import PlayerSetup from './components/PlayerSetup';
import PlayersGrid from './components/PlayersGrid';
import CurrentPlayerPanel from './components/CurrentPlayerPanel';

function App() {
  // --- États globaux du jeu ---
  const [players, setPlayers] = useState([]); // Liste des joueurs inscrits
  const [currentPlayerIndex, setCurrentPlayerIndex] = useState(0); // Indice du joueur actif
  const [gameStarted, setGameStarted] = useState(false); // Permet de verrouiller la config une fois la partie lancée
  const [winner, setWinner] = useState(null); // Stocke le joueur vainqueur, s'il existe
  const [newPlayerName, setNewPlayerName] = useState(''); // Champ de saisie pour ajouter un joueur
  const [currentTurn, setCurrentTurn] = useState([]); // Les 3 fléchettes du tour actuel
  const [turnScore, setTurnScore] = useState(0); // Somme des points du tour courant
  const [selectedMultiplier, setSelectedMultiplier] = useState(1); // Simple/Double/Triple pour la saisie manuelle
  const [directScoreInput, setDirectScoreInput] = useState(''); // Saisie d'un score total direct
  const [showDartboard, setShowDartboard] = useState(false); // Affiche ou non la cible interactive

  // Offset éventuel pour réaligner les secteurs de la cible avec l'image (en degrés)
  const angleOffset = 0;

  // Ajouter un nouveau joueur
  const addPlayer = () => {
    if (newPlayerName.trim() && !gameStarted) {
      const newPlayer = {
        id: Date.now(),
        name: newPlayerName.trim(),
        score: 301,
        initialScore: 301
      };
      setPlayers([...players, newPlayer]);
      setNewPlayerName('');
    }
  };

  // Supprimer un joueur
  const removePlayer = (playerId) => {
    if (!gameStarted) {
      setPlayers(players.filter(player => player.id !== playerId));
    }
  };

  // Commencer la partie
  const startGame = () => {
    if (players.length >= 2) {
      setGameStarted(true);
      setCurrentPlayerIndex(0);
      setWinner(null);
    }
  };

  // Réinitialiser la partie
  const resetGame = () => {
    setGameStarted(false);
    setCurrentPlayerIndex(0);
    setWinner(null);
    setCurrentTurn([]);
    setTurnScore(0);
    setSelectedMultiplier(1);
    setDirectScoreInput('');
    setPlayers(players.map(player => ({
      ...player,
      score: player.initialScore
    })));
  };

  // Ajouter une fléchette au tour actuel
  const addDart = (basePoints) => {
    if (!gameStarted || winner || currentTurn.length >= 3) return;

    const multiplier = selectedMultiplier;
    const dartScore = basePoints * multiplier;
    const newTurn = [...currentTurn, { points: dartScore, multiplier, basePoints }];
    const newTurnScore = turnScore + dartScore;
    
    setCurrentTurn(newTurn);
    setTurnScore(newTurnScore);
    
    // Revenir automatiquement en mode Simple après avoir utilisé Double ou Triple
    if (multiplier !== 1) {
      setSelectedMultiplier(1);
    }
  };

  // Valider le tour (appliquer les points au score du joueur)
  const validateTurn = () => {
    if (!gameStarted || winner || currentTurn.length === 0) return;

    const newPlayers = [...players];
    const currentPlayer = newPlayers[currentPlayerIndex];
    
    // Calculer le nouveau score
    const newScore = currentPlayer.score - turnScore;
    
    // Si le score devient négatif, annuler le tour et passer au joueur suivant
    if (newScore < 0) {
      // Le joueur a dépassé, annuler le tour
      setCurrentTurn([]);
      setTurnScore(0);
      nextPlayer();
      return;
    }
    
    // Si le score devient exactement 0, vérifier que la dernière fléchette est un double
    if (newScore === 0) {
      const lastDart = currentTurn[currentTurn.length - 1];
      if (lastDart.multiplier !== 2) {
        // Pas un double pour finir, annuler le tour
        setCurrentTurn([]);
        setTurnScore(0);
        nextPlayer();
        return;
      }
      // Victoire !
      setWinner(currentPlayer);
      return;
    }
    
    // Mettre à jour le score
    currentPlayer.score = newScore;
    setPlayers(newPlayers);
    
    // Réinitialiser le tour et passer au joueur suivant
    setCurrentTurn([]);
    setTurnScore(0);
    setSelectedMultiplier(1);
    setDirectScoreInput('');
    nextPlayer();
  };

  // Annuler le tour actuel
  const cancelTurn = () => {
    setCurrentTurn([]);
    setTurnScore(0);
    setSelectedMultiplier(1);
    setDirectScoreInput('');
  };

  // Appliquer un score direct
  const applyDirectScore = () => {
    if (!gameStarted || winner) return;
    
    const score = parseInt(directScoreInput);
    if (isNaN(score) || score < 0 || score > 180) {
      alert('Veuillez saisir un score valide entre 0 et 180');
      return;
    }

    const newPlayers = [...players];
    const currentPlayer = newPlayers[currentPlayerIndex];
    
    // Calculer le nouveau score
    const newScore = currentPlayer.score - score;
    
    // Si le score devient négatif, annuler et passer au joueur suivant
    if (newScore < 0) {
      setDirectScoreInput('');
      nextPlayer();
      return;
    }
    
    // Si le score devient exactement 0, vérifier que c'est un double (pour la saisie directe, on considère que c'est valide)
    if (newScore === 0) {
      // Victoire !
      setWinner(currentPlayer);
      setDirectScoreInput('');
      return;
    }
    
    // Mettre à jour le score
    currentPlayer.score = newScore;
    setPlayers(newPlayers);
    
    // Réinitialiser et passer au joueur suivant
    setCurrentTurn([]);
    setTurnScore(0);
    setSelectedMultiplier(1);
    setDirectScoreInput('');
    nextPlayer();
  };

  // Passer au joueur suivant
  const nextPlayer = () => {
    setCurrentPlayerIndex((prevIndex) => 
      (prevIndex + 1) % players.length
    );
  };

  // Retour au joueur précédent (pour corriger une erreur)
  const previousPlayer = () => {
    if (!gameStarted || winner) return;
    setCurrentPlayerIndex((prevIndex) => 
      prevIndex === 0 ? players.length - 1 : prevIndex - 1
    );
  };

  // Gérer le clic sur la cible
  const handleDartboardClick = (points, zone) => {
    if (!gameStarted || winner || currentTurn.length >= 3) return;

    let multiplier = 1;
    let basePoints = points;

    // Déterminer le multiplicateur selon la zone cliquée
    if (zone === 'double') {
      multiplier = 2;
    } else if (zone === 'triple') {
      multiplier = 3;
    } else if (zone === 'bull') {
      basePoints = 25;
      multiplier = 1;
    } else if (zone === 'bullseye') {
      basePoints = 25;
      multiplier = 2; // Bull's eye = double bull = 50 points
    }

    const dartScore = basePoints * multiplier;
    const newTurn = [...currentTurn, { points: dartScore, multiplier, basePoints, zone }];
    const newTurnScore = turnScore + dartScore;
    
    setCurrentTurn(newTurn);
    setTurnScore(newTurnScore);
    
    // Revenir automatiquement en mode Simple après avoir utilisé Double ou Triple
    if (multiplier !== 1) {
      setSelectedMultiplier(1);
    }
  };

  return (
    <div className="container">
      <h1 className="game-title">🎯 Fléchettes 301</h1>
      
      {!gameStarted && (
        <PlayerSetup
          players={players}
          newPlayerName={newPlayerName}
          onNameChange={setNewPlayerName}
          onAddPlayer={addPlayer}
          onRemovePlayer={removePlayer}
          onStartGame={startGame}
        />
      )}

      {gameStarted && !winner && (
        <div className="game-status">
          Tour de {players[currentPlayerIndex]?.name} - 
          Fléchettes: {currentTurn.length}/3 - 
          Score du tour: {turnScore}
        </div>
      )}

      {winner && (
        <div className="winner-message">
          🏆 {winner.name} a gagné ! 🏆
        </div>
      )}

      {gameStarted && (
        <PlayersGrid
          players={players}
          currentPlayerIndex={currentPlayerIndex}
          winner={winner}
          renderCurrentPanel={() => (
            <CurrentPlayerPanel
              currentTurn={currentTurn}
              turnScore={turnScore}
              selectedMultiplier={selectedMultiplier}
              onSelectMultiplier={setSelectedMultiplier}
              directScoreInput={directScoreInput}
              onChangeDirectScore={setDirectScoreInput}
              onApplyDirectScore={applyDirectScore}
              onAddDart={addDart}
              onValidateTurn={validateTurn}
              onCancelTurn={cancelTurn}
              showDartboard={showDartboard}
              onToggleDartboard={() => setShowDartboard((prev) => !prev)}
              canThrow={currentTurn.length < 3}
              onDartboardHit={handleDartboardClick}
              angleOffset={angleOffset}
            />
          )}
        />
      )}

      {gameStarted && (
        <div className="game-controls">
          <button 
            onClick={previousPlayer} 
            className="control-btn btn-secondary"
            disabled={winner}
          >
            Joueur précédent
          </button>
          <button 
            onClick={resetGame} 
            className="control-btn btn-danger"
          >
            Nouvelle partie
          </button>
        </div>
      )}
    </div>
  );
}

export default App;